import { useState, useEffect } from "react";
import { META_MODULE_ENABLED } from "../lib/features";
import { NavLink, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  QrCode,
  FileCheck2,
  Layers,
  BarChart3,
  CreditCard,
  Settings,
  LogOut,
  Search,
  Building2,
  MessageSquare,
  Send,
  Bot,
  Activity,
  PanelLeftClose,
  PanelLeftOpen,
  Sun,
  ShieldCheck,
  FileText,
  FlaskConical,
  Menu,
  X,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { listTenants, type CurrentUser, type Tenant } from "../lib/api";

import { OverviewView } from "./views/OverviewView";
import { TenantsView } from "./views/TenantsView";
import { ChannelsView } from "./views/ChannelsView";
import { QRInstancesView } from "./views/QRInstancesView";
import { QRMessageQueueView } from "./views/QRMessageQueueView";
import { DeliveryReportsDetailedView } from "./views/DeliveryReportsDetailedView";
import { UsageConnectionReportsView } from "./views/UsageConnectionReportsView";
import { GlobalDeliveryMonitorView } from "./views/GlobalDeliveryMonitorView";
import { UnifiedInboxView } from "./views/UnifiedInboxView";
import { ContactsView } from "./views/ContactsView";
import { CampaignsView } from "./views/CampaignsView";
import { TemplatesView } from "./views/TemplatesView";
import { AutomationsView } from "./views/AutomationsView";
import { TeamManagementView } from "./views/TeamManagementView";
import { ManualBillingView } from "./views/ManualBillingView";
import { SystemHealthView } from "./views/SystemHealthView";
import { QRApiTestView } from "./views/QRApiTestView";
import { UserProfileModal } from "./UserProfileModal";

type AdminConsoleProps = {
  token: string;
  user: CurrentUser;
  onLogout: () => void;
};

export function AdminConsole({ token, user, onLogout }: AdminConsoleProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarExpanded, setSidebarExpanded] = useState<boolean>(() => {
    return localStorage.getItem("wamaster_sidebar_expanded") === "true";
  });
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    setMobileDrawerOpen(false);
  }, [location.pathname]);

  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const isSuperAdmin = user.role === "superadmin" || user.role === "platform_admin";
  const isAgent = user.role === "agent";

  const tenantsQuery = useQuery({
    queryKey: ["admin", "tenants"],
    queryFn: () => listTenants(token),
    enabled: isSuperAdmin,
  });

  const [selectedTenantId, setSelectedTenantId] = useState<string>(() => {
    return localStorage.getItem("wamaster_selected_tenant_id") || (user.tenant_id || "all");
  });
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const activeTenantId = isSuperAdmin
    ? (selectedTenantId === "all" ? undefined : selectedTenantId)
    : user.tenant_id;

  function handleSelectTenant(id: string) {
    setSelectedTenantId(id);
    localStorage.setItem("wamaster_selected_tenant_id", id);
  }


  function toggleSidebar() {
    setSidebarExpanded((prev) => {
      const next = !prev;
      localStorage.setItem("wamaster_sidebar_expanded", String(next));
      return next;
    });
  }

  const navGroups = isAgent
    ? [
        {
          title: "Agent Workspace",
          items: [
            { path: "/admin/inbox", label: "Live Inbox", icon: MessageSquare },
            { path: "/admin/contacts", label: "Contacts CRM", icon: Users },
            { path: "/admin/templates", label: "Quick Replies & Templates", icon: FileText },
          ],
        },
      ]
    : [
        {
          title: "Core Platform",
          items: [
            { path: "/admin/overview", label: "Dashboard", icon: LayoutDashboard },
            { path: "/admin/team", label: "Team & Agents", icon: Users },
            { path: "/admin/qr-instances", label: "Instances & QR", icon: QrCode },
            { path: "/admin/qr-api-test", label: "Inbox API Lab", icon: FlaskConical },
            ...(META_MODULE_ENABLED ? [{ path: "/admin/channels", label: "Meta Official WABA", icon: ShieldCheck }] : []),
          ],
        },
        {
          title: "Live Operations",
          items: [
            { path: "/admin/inbox", label: "Live Inbox", icon: MessageSquare },
            { path: "/admin/delivery-reports", label: "Delivery Reports", icon: FileCheck2 },
            { path: "/admin/qr-queue", label: "Message Queue", icon: Layers },
            { path: "/admin/usage-connections", label: "Usage & Telemetry", icon: BarChart3 },
          ],
        },
        {
          title: "CRM & Growth",
          items: [
            { path: "/admin/contacts", label: "Contacts CRM", icon: Users },
            { path: "/admin/templates", label: "Message Templates", icon: FileText },
            { path: "/admin/campaigns", label: "Broadcast Campaigns", icon: Send },
            { path: "/admin/automations", label: "Bots & Automations", icon: Bot },
            { path: "/admin/billing", label: "Recharge & Billing", icon: CreditCard },
          ],
        },
        ...(isSuperAdmin
          ? [
              {
                title: "Super Admin Control Plane",
                items: [
                  { path: "/admin/tenants", label: "Organizations & Quotas", icon: Building2 },
                  { path: "/admin/global-delivery", label: "Global Fleet Health", icon: Activity },
                  { path: "/admin/system", label: "System Health", icon: Settings },
                ],
              },
            ]
          : [
              {
                title: "System",
                items: [{ path: "/admin/system", label: "System Diagnostics", icon: Settings }],
              },
            ]),
      ];

  const quickSearchItems = isAgent
    ? [
        { name: "Live Inbox", path: "/admin/inbox", cat: "Chat" },
        { name: "Contacts CRM", path: "/admin/contacts", cat: "CRM" },
        { name: "Quick Replies & Templates", path: "/admin/templates", cat: "Templates" },
      ]
    : [
        { name: "Dashboard Overview", path: "/admin/overview", cat: "eNotify" },
        { name: "Team & Agents (Unlimited Seats)", path: "/admin/team", cat: "Team" },
        { name: "WhatsApp Instances & Pairing", path: "/admin/qr-instances", cat: "eNotify" },
        { name: "Inbox API Lab", path: "/admin/qr-api-test", cat: "Developer" },
        ...(META_MODULE_ENABLED ? [{ name: "Meta Verified Official Channels", path: "/admin/channels", cat: "Meta WABA" }] : []),
        { name: "Delivery Reports (Granular Timestamps)", path: "/admin/delivery-reports", cat: "eNotify" },
        { name: "Message Queue & Pacing", path: "/admin/qr-queue", cat: "eNotify" },
        { name: "Today's Usage & Sockets Telemetry", path: "/admin/usage-connections", cat: "eNotify" },
        { name: "Recharge History & Wallet", path: "/admin/billing", cat: "eNotify" },
        { name: "Live Inbox", path: "/admin/inbox", cat: "Chat" },
        { name: "Contacts CRM & Bulk Import", path: "/admin/contacts", cat: "CRM" },
        { name: "Campaign Broadcast Studio", path: "/admin/campaigns", cat: "Broadcasting" },
        { name: "Keyword Auto-Responders & Bots", path: "/admin/automations", cat: "Automations" },
        ...(isSuperAdmin ? [{ name: "Organizations & Quotas", path: "/admin/tenants", cat: "Admin" }] : []),
        ...(isSuperAdmin ? [{ name: "Global Fleet Monitor", path: "/admin/global-delivery", cat: "Admin" }] : []),
      ];

  const filteredSearch = quickSearchItems.filter(
    (i) =>
      i.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      i.cat.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 flex antialiased">
      {/* Mobile Drawer Backdrop */}
      {mobileDrawerOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-40 md:hidden transition-opacity"
          onClick={() => setMobileDrawerOpen(false)}
        />
      )}

      {/* Mobile Slide-Over Sidebar Drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-2xl flex flex-col justify-between transition-transform duration-300 ease-in-out md:hidden ${
          mobileDrawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Mobile Sidebar Header */}
          <div className="h-14 border-b border-slate-100 flex items-center justify-between px-4 shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-black text-sm shadow-sm shrink-0">
                WA
              </div>
              <div>
                <span className="font-extrabold text-slate-900 text-sm tracking-tight block">WA Master</span>
                <span className="text-[10px] text-slate-400 font-mono block">Enterprise v1.1</span>
              </div>
            </div>
            <button
              onClick={() => setMobileDrawerOpen(false)}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
              title="Close navigation"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation Groups List */}
          <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-4">
            {navGroups.map((group, gIdx) => (
              <div key={gIdx} className="space-y-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2.5 py-1">
                  {group.title}
                </div>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileDrawerOpen(false)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-semibold transition-all ${
                        isActive
                          ? "bg-emerald-50 text-emerald-700 font-bold border border-emerald-200 shadow-2xs"
                          : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                      }`}
                    >
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-emerald-600" : "text-slate-400"}`} />
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Bottom Logout & Profile */}
          <div className="p-3 border-t border-slate-100 shrink-0 space-y-2">
            <button
              onClick={() => {
                setMobileDrawerOpen(false);
                setIsProfileModalOpen(true);
              }}
              className="w-full text-left p-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200/60 flex items-center gap-2.5 transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-700 font-bold text-xs flex items-center justify-center shrink-0">
                {user.username.slice(0, 2).toUpperCase()}
              </div>
              <div className="truncate text-xs">
                <div className="font-bold text-slate-900 truncate">{user.username}</div>
                <div className="text-[10px] text-slate-400 font-mono capitalize">{user.role}</div>
              </div>
            </button>
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-all cursor-pointer"
            >
              <LogOut className="w-4 h-4 shrink-0 text-slate-400 hover:text-rose-600" />
              <span>Log Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* 1. Desktop Expandable / Collapsible Side Drawer (Hidden on Mobile) */}
      <aside
        className={`bg-white border-r border-slate-200/90 hidden md:flex flex-col justify-between transition-all duration-200 z-30 sticky top-0 h-screen shrink-0 ${
          sidebarExpanded ? "w-64" : "w-16"
        }`}
      >
        <div className="flex flex-col h-full overflow-hidden">
          
          {/* Sidebar Top Header (Brand + Toggle Button) */}
          <div className="h-14 border-b border-slate-100 flex items-center justify-between px-3 shrink-0">
            <div className="flex items-center gap-2.5 overflow-hidden">
              <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-black text-sm shadow-sm shrink-0">
                WA
              </div>
              {sidebarExpanded && (
                <div className="truncate">
                  <span className="font-extrabold text-slate-900 text-sm tracking-tight block">WA Master</span>
                  <span className="text-[10px] text-slate-400 font-mono block">Enterprise v1.1</span>
                </div>
              )}
            </div>

            <button
              onClick={toggleSidebar}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
              title={sidebarExpanded ? "Collapse Sidebar" : "Expand Sidebar"}
            >
              {sidebarExpanded ? (
                <PanelLeftClose className="w-4 h-4" />
              ) : (
                <PanelLeftOpen className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Navigation Groups List */}
          <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
            {navGroups.map((group, gIdx) => (
              <div key={gIdx} className="space-y-1">
                {sidebarExpanded && (
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2.5 py-1">
                    {group.title}
                  </div>
                )}
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      title={item.label}
                      className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-xs font-medium transition-all ${
                        isActive
                          ? "bg-emerald-50 text-emerald-700 font-bold border border-emerald-200 shadow-2xs"
                          : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                      }`}
                    >
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-emerald-600" : "text-slate-400"}`} />
                      {sidebarExpanded && (
                        <span className="truncate whitespace-nowrap">{item.label}</span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* Bottom Logout & User Summary */}
          <div className="p-2 border-t border-slate-100 shrink-0">
            {sidebarExpanded && (
              <button
                onClick={() => setIsProfileModalOpen(true)}
                title="Manage Profile & Security"
                className="w-full text-left p-2 mb-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200/60 flex items-center gap-2 transition-colors cursor-pointer"
              >
                <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-700 font-bold text-xs flex items-center justify-center shrink-0">
                  {user.username.slice(0, 2).toUpperCase()}
                </div>
                <div className="truncate text-xs">
                  <div className="font-bold text-slate-900 truncate">{user.username}</div>
                  <div className="text-[10px] text-slate-400 font-mono capitalize">{user.role}</div>
                </div>
              </button>
            )}
            <button
              onClick={onLogout}
              title="Logout from session"
              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-semibold text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-all cursor-pointer"
            >
              <LogOut className="w-4 h-4 shrink-0 text-slate-400 hover:text-rose-600" />
              {sidebarExpanded && <span>Log Out</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* 2. Main Content Wrapper */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header Bar (Matches eNotify Header Exactly) */}
        <header className="h-14 bg-white border-b border-slate-200/90 flex items-center justify-between px-3 sm:px-4 sticky top-0 z-20 shadow-2xs">
          {/* Left: Sidebar Toggle + Organization Identifier */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {/* Mobile Hamburger Menu */}
            <button
              onClick={() => setMobileDrawerOpen(true)}
              className="md:hidden p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-slate-900 transition-colors cursor-pointer shrink-0"
              title="Open navigation menu"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Desktop Sidebar Toggle */}
            <button
              onClick={toggleSidebar}
              className="hidden md:flex p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors cursor-pointer shrink-0"
              title={sidebarExpanded ? "Collapse Sidebar" : "Expand Sidebar"}
            >
              {sidebarExpanded ? <PanelLeftClose className="w-4 h-4" /> : <PanelLeftOpen className="w-4 h-4" />}
            </button>

            <div className="h-4 w-px bg-slate-200 hidden sm:block" />

            {/* Organization Switcher Dropdown */}
            <div className="flex items-center gap-2 min-w-0">
              <Building2 className="w-4 h-4 text-emerald-600 hidden sm:block shrink-0" />
              {isSuperAdmin ? (
                <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                  <select
                    value={selectedTenantId}
                    onChange={(e) => handleSelectTenant(e.target.value)}
                    className="bg-slate-50 border border-slate-200 text-slate-800 text-xs font-bold rounded-lg px-2 py-1 focus:outline-none focus:border-emerald-500 transition-all cursor-pointer max-w-[140px] sm:max-w-none truncate"
                    title="Active Organization Workspace"
                  >
                    <option value="all">🌐 All Orgs</option>
                    {(tenantsQuery.data || []).map((t: Tenant) => (
                      <option key={t.id} value={t.id}>
                        🏢 {t.name} ({t.slug})
                      </option>
                    ))}
                  </select>
                  <span className="px-1.5 sm:px-2 py-0.5 rounded-md bg-purple-100 border border-purple-200 text-purple-800 text-[9px] sm:text-[10px] font-bold tracking-wide uppercase shrink-0">
                    SUPER
                  </span>
                </div>
              ) : isAgent ? (
                <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                  <span className="text-slate-800 font-bold text-xs truncate">
                    {user.tenant_name || "Workspace"}
                  </span>
                  <span className="px-1.5 sm:px-2 py-0.5 rounded-md bg-sky-50 border border-sky-200 text-sky-700 text-[9px] sm:text-[10px] font-bold tracking-wide uppercase shrink-0 hidden xs:inline-block">
                    AGENT
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                  <span className="text-slate-800 font-bold text-xs truncate">
                    {user.tenant_name || "Primary Tenant"}
                  </span>
                  <span className="px-1.5 sm:px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-[9px] sm:text-[10px] font-bold tracking-wide uppercase shrink-0 hidden xs:inline-block">
                    ADMIN
                  </span>
                </div>
              )}
            </div>
          </div>


          {/* Right: Quick Search + Theme Switcher + Profile */}
          <div className="flex items-center gap-2.5">
            {/* Quick Search Shortcut ⌘k */}
            <button
              onClick={() => setSearchModalOpen(true)}
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-400 hover:text-slate-700 hover:border-slate-300 transition-all shadow-2xs"
            >
              <Search className="w-3.5 h-3.5 text-slate-400" />
              <span>Search modules...</span>
              <kbd className="px-1.5 py-0.5 rounded bg-white border border-slate-200 text-[10px] font-mono text-slate-500 font-bold">
                ⌘K
              </kbd>
            </button>

            {/* Theme Toggle (Light Default) */}
            <button className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors">
              <Sun className="w-4 h-4 text-amber-500" />
            </button>

            {/* User Profile Avatar */}
            <button
              onClick={() => setIsProfileModalOpen(true)}
              title="My Profile & Security"
              className="w-8 h-8 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-bold text-xs flex items-center justify-center border border-emerald-300 shadow-2xs transition-colors cursor-pointer"
            >
              {user.username.slice(0, 2).toUpperCase()}
            </button>
          </div>
        </header>

        {/* 3. Main Views Area */}
        <main className={`flex-1 ${location.pathname.startsWith("/admin/inbox") || location.pathname === "/admin" || location.pathname === "/" ? "p-2 sm:p-3 md:p-5" : "p-5 md:p-6"} overflow-x-hidden`}>
          <Routes>
            {isAgent ? (
              <>
                <Route path="/admin/inbox" element={<UnifiedInboxView token={token} user={user} tenantId={activeTenantId} />} />
                <Route path="/admin/contacts" element={<ContactsView token={token} user={user} tenantId={activeTenantId} />} />
                <Route path="/admin/templates" element={<TemplatesView token={token} user={user} />} />
                <Route path="/admin" element={<Navigate to="/admin/inbox" replace />} />
                <Route path="/" element={<Navigate to="/admin/inbox" replace />} />
                <Route path="*" element={<Navigate to="/admin/inbox" replace />} />
              </>
            ) : (
              <>
                <Route path="/admin/overview" element={<OverviewView token={token} user={user} tenantId={activeTenantId} />} />
                <Route path="/admin/team" element={<TeamManagementView token={token} user={user} />} />
                <Route path="/admin/qr-instances" element={<QRInstancesView token={token} user={user} tenantId={activeTenantId} />} />
                <Route path="/admin/qr-api-test" element={<QRApiTestView token={token} user={user} tenantId={activeTenantId} />} />
                <Route path="/admin/channels" element={META_MODULE_ENABLED ? <ChannelsView token={token} user={user} /> : <Navigate to="/admin/qr-instances" replace />} />
                <Route path="/admin/delivery-reports" element={<DeliveryReportsDetailedView token={token} user={user} />} />
                <Route path="/admin/qr-queue" element={<QRMessageQueueView token={token} user={user} tenantId={activeTenantId} />} />
                <Route path="/admin/usage-connections" element={<UsageConnectionReportsView token={token} user={user} tenantId={activeTenantId} />} />
                <Route path="/admin/billing" element={<ManualBillingView token={token} user={user} tenantId={activeTenantId} />} />
                <Route path="/admin/inbox" element={<UnifiedInboxView token={token} user={user} tenantId={activeTenantId} />} />
                <Route path="/admin/contacts" element={<ContactsView token={token} user={user} tenantId={activeTenantId} />} />
                <Route path="/admin/templates" element={<TemplatesView token={token} user={user} />} />
                <Route path="/admin/campaigns" element={<CampaignsView token={token} user={user} tenantId={activeTenantId} />} />
                <Route path="/admin/automations" element={<AutomationsView token={token} user={user} />} />
                {isSuperAdmin && (
                  <>
                    <Route path="/admin/tenants" element={<TenantsView token={token} user={user} />} />
                    <Route path="/admin/global-delivery" element={<GlobalDeliveryMonitorView token={token} user={user} />} />
                  </>
                )}
                <Route path="/admin/system" element={<SystemHealthView token={token} user={user} />} />
                <Route path="/admin" element={<Navigate to="/admin/overview" replace />} />
                <Route path="/" element={<Navigate to="/admin/overview" replace />} />
                <Route path="*" element={<Navigate to="/admin/overview" replace />} />
              </>
            )}
          </Routes>
        </main>

        {/* eNotify Support Footer */}
        <footer className={`h-10 bg-white border-t border-slate-200/90 items-center justify-between px-5 text-[11px] text-slate-400 ${location.pathname.startsWith("/admin/inbox") ? "hidden lg:flex" : "hidden sm:flex"}`}>
          <div>WA Master Platform · Enterprise High Throughput Engine</div>
          <div className="flex items-center gap-3">
            <span>support@wamaster.io</span>
            <span>+91 919028083335</span>
          </div>
        </footer>
      </div>

      {/* 4. Global Quick Search Modal (⌘K) */}
      {searchModalOpen && (
        <div className="modal-backdrop" onClick={() => setSearchModalOpen(false)}>
          <div className="modal-card max-w-lg p-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-3 border-b border-slate-200 flex items-center gap-2 bg-slate-50">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search modules, reports, instances, CRM..."
                className="w-full bg-transparent text-xs text-slate-900 focus:outline-none"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              <button onClick={() => setSearchModalOpen(false)} className="text-slate-400 text-xs font-bold px-1.5">
                ESC
              </button>
            </div>

            <div className="max-h-72 overflow-y-auto p-2 divide-y divide-slate-100">
              {filteredSearch.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    navigate(item.path);
                    setSearchModalOpen(false);
                  }}
                  className="w-full text-left p-2 rounded-lg hover:bg-slate-50 flex items-center justify-between text-xs transition-colors"
                >
                  <span className="font-semibold text-slate-800">{item.name}</span>
                  <span className="text-[10px] font-mono text-slate-400 px-1.5 py-0.5 rounded bg-slate-100">
                    {item.cat}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 5. User Profile & Password Security Modal */}
      <UserProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        token={token}
        user={user}
      />
    </div>
  );
}
